import { useState } from 'react'
import { Code, Copy, Check, Play, Leaf, ExternalLink, AlertCircle } from 'lucide-react'
import { supabase } from '../lib/supabase'

function ApiDocs() {
  const [copiedIndex, setCopiedIndex] = useState(null)
  const [testQuery, setTestQuery] = useState('')
  const [testResult, setTestResult] = useState(null)
  const [testLoading, setTestLoading] = useState(false)

  const apiBaseUrl = window.location.origin
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedIndex(index)
      setTimeout(() => setCopiedIndex(null), 2000)
    } catch (err) {
      console.error('复制失败:', err)
    }
  }

  const handleTest = async () => {
    if (!testQuery.trim()) return
    
    setTestLoading(true)
    setTestResult(null)
    
    try {
      if (isDev) {
        const isNumeric = /^\d+$/.test(testQuery.trim())
        let query = supabase
          .from('tobacco_products')
          .select('*')
          .limit(5)
        
        if (isNumeric) {
          query = query.or(`small_box_barcode.eq.${testQuery.trim()},carton_barcode.eq.${testQuery.trim()}`)
        } else {
          const keywords = testQuery.trim().split(/\s+/).filter(Boolean)
          keywords.forEach(kw => {
            query = query.ilike('name', `%${kw}%`)
          })
        }
        
        const { data, error } = await query
        
        if (error) throw error
        
        setTestResult({
          success: true,
          count: data.length,
          search_type: isNumeric ? 'barcode' : 'name',
          query: testQuery,
          data: data,
          _note: '开发环境：使用 Supabase 客户端直接查询'
        })
      } else {
        const response = await fetch(`${apiBaseUrl}/api/v1/search?q=${encodeURIComponent(testQuery)}`)
        const data = await response.json()
        setTestResult(data)
      }
    } catch (err) {
      setTestResult({ success: false, error: err.message })
    } finally {
      setTestLoading(false)
    }
  }

  const CodeBlock = ({ code, index }) => (
    <div className="relative bg-gray-900 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800">
        <span className="text-xs text-gray-400 font-mono">HTTP</span>
        <button
          onClick={() => copyToClipboard(code, index)}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
        >
          {copiedIndex === index ? (
            <>
              <Check className="w-4 h-4" />
              已复制
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              复制
            </>
          )}
        </button>
      </div>
      <pre className="p-4 text-sm text-green-400 overflow-x-auto">
        <code>{code}</code>
      </pre>
    </div>
  )

  const endpoints = [
    {
      title: '通用查询',
      desc: '自动识别条码或名称进行查询',
      param: 'q',
      example: `${apiBaseUrl}/api/v1/search?q=中华`,
      exampleCode: `curl "${apiBaseUrl}/api/v1/search?q=中华"`,
    },
    {
      title: '条码查询',
      desc: '通过小盒条码或条盒条码精确查询',
      param: 'barcode',
      example: `${apiBaseUrl}/api/v1/search?barcode=6901028075766`,
      exampleCode: `curl "${apiBaseUrl}/api/v1/search?barcode=6901028075766"`,
    },
    {
      title: '名称查询',
      desc: '通过产品名称模糊查询（名称中的空格作为名称的一部分）',
      param: 'name',
      example: `${apiBaseUrl}/api/v1/search?name=中华`,
      exampleCode: `curl "${apiBaseUrl}/api/v1/search?name=中华"`,
    },
    {
      title: '多关键词查询',
      desc: '通过产品名称模糊查询，多个关键词用 | 分隔（AND 逻辑）',
      param: 'keywords',
      example: `${apiBaseUrl}/api/v1/search?keywords=中华|细支`,
      exampleCode: `curl "${apiBaseUrl}/api/v1/search?keywords=中华|细支"`,
    },
  ]

  const parameters = [
    { name: 'q', type: 'string', desc: '通用查询，支持条码或名称（名称中的空格作为名称的一部分）', required: false },
    { name: 'barcode', type: 'string', desc: '条码查询，支持小盒条码和条盒条码', required: false },
    { name: 'name', type: 'string', desc: '名称查询（名称中的空格作为名称的一部分）', required: false },
    { name: 'keywords', type: 'string', desc: '多关键词查询，多个关键词用 | 分隔（AND 逻辑）', required: false },
    { name: 'limit', type: 'integer', desc: '返回数量限制，默认 5，最大 5', required: false },
  ]

  const responseFields = [
    { name: 'success', type: 'boolean', desc: '请求是否成功' },
    { name: 'count', type: 'integer', desc: '返回结果数量' },
    { name: 'search_type', type: 'string', desc: '查询类型：barcode、name 或 keywords' },
    { name: 'query', type: 'string', desc: '原始查询参数' },
    { name: 'data', type: 'array', desc: '产品数据数组' },
  ]

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl mb-4 shadow-lg shadow-blue-300/40">
            <Code className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">API 文档</h1>
          <p className="text-gray-600">烟草信息查询 API 接口说明</p>
        </div>

        <div className="bg-white/90 backdrop-blur-sm border border-blue-200/60 rounded-3xl p-6 sm:p-8 shadow-xl shadow-blue-100/30 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Leaf className="w-5 h-5 text-green-500" />
            接口概述
          </h2>
          <div className="space-y-3 text-gray-600">
            <p>
              <span className="font-medium text-gray-800">基础地址：</span>
              <code className="ml-2 px-3 py-1 bg-gray-100 rounded-lg text-sm font-mono text-blue-600">
                {apiBaseUrl}/api/v1/search
              </code>
            </p>
            <p>
              <span className="font-medium text-gray-800">请求方式：</span>
              <code className="ml-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-medium">GET</code>
            </p>
            <p>
              <span className="font-medium text-gray-800">返回格式：</span>
              <code className="ml-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium">JSON</code>
            </p>
            <p>
              <span className="font-medium text-gray-800">认证方式：</span>
              <span className="ml-2 text-sm">无需认证，公开接口</span>
            </p>
          </div>
          {isDev && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">开发环境提示</p>
                <p className="mt-1">当前处于开发环境，在线测试功能使用 Supabase 客户端直接查询。生产环境部署到 EdgeOne Pages 后，API 将通过 Node Functions 提供服务。</p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white/90 backdrop-blur-sm border border-green-200/60 rounded-3xl p-6 sm:p-8 shadow-xl shadow-green-100/30 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6">接口端点</h2>
          <div className="space-y-6">
            {endpoints.map((endpoint, index) => (
              <div key={index} className="border border-gray-200/60 rounded-2xl p-5 hover:border-blue-300 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-gray-800">{endpoint.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{endpoint.desc}</p>
                  </div>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    ?{endpoint.param}=
                  </span>
                </div>
                <div className="mb-3">
                  <p className="text-xs text-gray-400 mb-1">示例请求：</p>
                  <CodeBlock code={endpoint.exampleCode} index={`endpoint-${index}`} />
                </div>
                <a
                  href={endpoint.example}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  在浏览器中打开
                </a>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white/90 backdrop-blur-sm border border-amber-200/60 rounded-3xl p-6 sm:p-8 shadow-xl shadow-amber-100/30 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6">请求参数</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">参数名</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">类型</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">必填</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">说明</th>
                </tr>
              </thead>
              <tbody>
                {parameters.map((param, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <code className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-mono">
                        {param.name}
                      </code>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{param.type}</td>
                    <td className="py-3 px-4">
                      {param.required ? (
                        <span className="text-red-500 text-xs">是</span>
                      ) : (
                        <span className="text-gray-400 text-xs">否</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{param.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white/90 backdrop-blur-sm border border-sky-200/60 rounded-3xl p-6 sm:p-8 shadow-xl shadow-sky-100/30 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6">返回字段</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">字段名</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">类型</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">说明</th>
                </tr>
              </thead>
              <tbody>
                {responseFields.map((field, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <code className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-mono">
                        {field.name}
                      </code>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{field.type}</td>
                    <td className="py-3 px-4 text-gray-600">{field.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-6 sm:p-8 shadow-xl mb-8">
          <h2 className="text-xl font-bold text-white mb-6">返回示例</h2>
          <pre className="text-sm text-green-400 overflow-x-auto">
            <code>{`{
  "success": true,
  "count": 2,
  "search_type": "name",
  "query": "中华",
  "data": [
    {
      "id": 1,
      "name": "中华(软)",
      "product_type": "烤烟型",
      "tar_content": 11,
      "nicotine_content": 1.0,
      "carbon_monoxide_content": 11,
      "small_box_barcode": "6901028075766",
      "carton_barcode": "6901028075773",
      "small_box_price": "65元/盒",
      "carton_price": "650元/条",
      "cigarette_type": "粗支",
      ...
    },
    ...
  ]
}`}</code>
          </pre>
        </div>

        <div className="bg-white/90 backdrop-blur-sm border border-indigo-200/60 rounded-3xl p-6 sm:p-8 shadow-xl shadow-indigo-100/30">
          <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Play className="w-5 h-5 text-indigo-500" />
            在线测试
          </h2>
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={testQuery}
              onChange={(e) => setTestQuery(e.target.value)}
              placeholder="输入查询关键词或条码..."
              className="flex-1 px-4 py-3 border-2 border-indigo-200 rounded-xl focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 transition-all duration-300 outline-none"
              onKeyDown={(e) => e.key === 'Enter' && handleTest()}
            />
            <button
              onClick={handleTest}
              disabled={testLoading || !testQuery.trim()}
              className="px-6 py-3 bg-gradient-to-r from-indigo-400 to-purple-500 text-white rounded-xl font-medium shadow-md shadow-indigo-300/30 hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testLoading ? '查询中...' : '测试'}
            </button>
          </div>
          
          {testResult && (
            <div className={`p-4 rounded-xl ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm font-medium mb-2 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {testResult.success ? `查询成功，返回 ${testResult.count} 条结果` : `查询失败: ${testResult.error}`}
              </p>
              <pre className="text-xs text-gray-600 overflow-x-auto max-h-60">
                <code>{JSON.stringify(testResult, null, 2)}</code>
              </pre>
            </div>
          )}
        </div>

        <p className="text-center text-gray-500 text-sm mt-8">
          如有问题或建议，请联系管理员
        </p>
      </div>
    </div>
  )
}

export default ApiDocs
